import { SizingProps } from '@mui/system'

import { Box, BoxProps } from '@/atoms/System/Box'
import styled, { defaultAttrs } from '@/styles'

export type CenterProps = BoxProps

/** Designed like https://www.chakra-ui.com/docs/components/center */
export const Center = styled(Box).attrs<CenterProps>((p: CenterProps) =>
	defaultAttrs(p, {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
	}),
)``

Center.displayName = 'Center'

export type AbsoluteCenterProps = CenterProps & {
	/** @default 'both' */
	axis?: 'both' | 'horizontal' | 'vertical'
}

const absoluteCenterVariants = {
	horizontal: {
		left: '50%',
		transform: 'translateX(-50%)',
	},
	vertical: {
		top: '50%',
		transform: 'translateY(-50%)',
	},
	both: {
		left: '50%',
		top: '50%',
		transform: 'translate(-50%, -50%)',
	},
} as const satisfies Record<Required<AbsoluteCenterProps>['axis'], CenterProps>

/** Designed like https://www.chakra-ui.com/docs/components/center */
export const AbsoluteCenter = styled(Box).attrs<AbsoluteCenterProps>((p: AbsoluteCenterProps) =>
	defaultAttrs(p, {
		position: 'absolute',
		...absoluteCenterVariants[(p.axis ?? 'both') as keyof typeof absoluteCenterVariants],
	}),
)``

AbsoluteCenter.displayName = 'AbsoluteCenter'

export type SquareProps = BoxProps & { size?: SizingProps['width'] }

export const Square = styled(Box).attrs<SquareProps>((p: SquareProps) =>
	defaultAttrs(p, {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		flexGrow: 0,
		height: p.size,
		justifyContent: 'center',
		width: p.size,
	}),
)<SquareProps>``

Square.displayName = 'Square'

export type CircleProps = SquareProps

export const Circle = styled(Square).attrs<CircleProps>((p: CircleProps) =>
	defaultAttrs(p, { borderRadius: '9999px' }),
)<CircleProps>``

Circle.displayName = 'Circle'
